import React, { useState } from 'react';

import { Banner, FormSection, Panel, SectionLabel } from '../clients-ui';

export const PRICING_FIELDS = [
  {
    key: 'price_per_vehicle',
    label: 'Prix par véhicule',
    unit: '€ HT / véhicule / mois',
    step: '1',
  },
  {
    key: 'price_per_sms',
    label: 'Prix par SMS',
    unit: '€ HT / SMS',
    step: '0.01',
  },
  {
    key: 'price_per_email',
    label: 'Prix par email',
    unit: '€ HT / email',
    step: '0.01',
  },
  {
    key: 'price_per_signature',
    label: 'Prix par signature électronique',
    unit: '€ HT / signature',
    step: '0.01',
  },
];

export const PRICING_KEYS = PRICING_FIELDS.map(({ key }) => key);

const isPriceInvalid = (value) =>
  value == null ||
  value === '' ||
  Number.isNaN(Number(value)) ||
  Number(value) < 0;

export const isPricingValid = (pricing) =>
  PRICING_FIELDS.every(({ key }) => !isPriceInvalid(pricing[key]));

const formatPrice = (value) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
    value
  );

const inputClassName =
  'tw-h-8 tw-w-[120px] tw-rounded-[5px] tw-border tw-border-solid tw-bg-white tw-px-2.5 tw-text-right tw-text-[13px] focus:tw-border-input-borderFocused focus:tw-outline-none';

/**
 * Rappel affiché partout où la tarification se modifie : les factures sont
 * calculées avec les tarifs en vigueur au moment de leur génération, et une
 * facture déjà générée n'est pas recalculée.
 */
export const PricingNotRetroactiveNotice = () => (
  <Banner tone="warn">
    Modification non rétroactive : les nouveaux tarifs s’appliquent aux factures
    générées après l’enregistrement, y compris celle du mois en cours si elle
    n’est pas encore générée. Les factures déjà générées gardent les tarifs de
    l’époque (sauf recalcul explicite).
  </Banner>
);

export default function PricingStep({ pricing, setPricing, isEdition }) {
  const [simulation, setSimulation] = useState({
    vehicles: 10,
    sms: 500,
    emails: 1000,
    signatures: 200,
  });

  const total =
    Number(pricing.price_per_vehicle || 0) * simulation.vehicles +
    Number(pricing.price_per_sms || 0) * simulation.sms +
    Number(pricing.price_per_email || 0) * simulation.emails +
    Number(pricing.price_per_signature || 0) * simulation.signatures;

  return (
    <Panel>
      <FormSection
        title="Tarifs"
        description="Utilisés pour la génération des factures mensuelles du client."
      >
        {isEdition && <PricingNotRetroactiveNotice />}
        <div className="tw-overflow-hidden tw-rounded-[6px] tw-border tw-border-solid tw-border-[#e6e8ec]">
          {PRICING_FIELDS.map(({ key, label, unit, step }) => {
            const isInvalid = isPriceInvalid(pricing[key]);

            return (
              <label
                key={key}
                className="tw-flex tw-items-center tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-[#eeeff2] tw-px-3 tw-py-2 last:tw-border-b-0"
              >
                <span className="tw-flex-1 tw-text-[13px] tw-text-black">
                  {label}
                </span>
                <input
                  type="number"
                  min="0"
                  step={step}
                  value={pricing[key] ?? ''}
                  onChange={(event) =>
                    setPricing((previous) => ({
                      ...previous,
                      [key]: event.target.value,
                    }))
                  }
                  className={`${inputClassName} ${
                    isInvalid
                      ? 'tw-border-input-required'
                      : 'tw-border-input-border'
                  }`}
                />
                <span className="tw-w-[160px] tw-text-[11px] tw-text-[#76808c]">
                  {unit}
                </span>
              </label>
            );
          })}
        </div>
      </FormSection>

      <FormSection
        title="Simulation"
        description="Estimation d’une facture mensuelle, à titre indicatif."
      >
        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-4">
          {[
            ['vehicles', 'Véhicules'],
            ['sms', 'SMS'],
            ['emails', 'Emails'],
            ['signatures', 'Signatures'],
          ].map(([key, label]) => (
            <label key={key} className="tw-flex tw-flex-col tw-gap-2">
              <SectionLabel>{label}</SectionLabel>
              <input
                type="number"
                min="0"
                value={simulation[key]}
                onChange={(event) =>
                  setSimulation((previous) => ({
                    ...previous,
                    [key]: Math.max(0, Number(event.target.value) || 0),
                  }))
                }
                className={`${inputClassName} tw-border-input-border`}
              />
            </label>
          ))}
          <div className="tw-ml-auto tw-flex tw-flex-col tw-items-end tw-gap-1">
            <SectionLabel>Total estimé</SectionLabel>
            <span className="tw-text-[20px] tw-font-semibold tw-text-main-active">
              {formatPrice(total)} HT
            </span>
          </div>
        </div>
      </FormSection>
    </Panel>
  );
}
